/**
 * UI Slice — navigation, theme, and transient UI flags.
 *
 * Navigation has no URL router (see CLAUDE.md): `screen` is a plain string and
 * `screenHistory` is a manually-maintained stack. `setCurrentScreen` appends
 * only when navigating to a *new* screen (avoids duplicate stack entries when
 * re-setting the current screen), and `handleBack` pops one level.
 */

import type { UIState, UIActions } from '@/types/store';

export const uiSlice: UIState = {
  screen: 'login',
  screenHistory: ['login'],
  darkMode: false,
  theme: 'x-men',
  isDragging: false,
  isFetchingGps: false,
  floorPlanDrawerProjectId: null,
};

export const uiActions: UIActions = (set, get) => ({
  /**
   * Navigate to `screen`. Pushes onto the history stack only when it differs
   * from the current top, so repeated calls to the same screen don't inflate
   * the back stack.
   */
  setCurrentScreen: (screen: string): void => {
    const history = get().screenHistory as string[];
    if (screen !== history[history.length - 1]) {
      set({ screen, screenHistory: [...history, screen] });
    } else {
      set({ screen });
    }
  },

  /** Pop one level off the navigation stack. No-op when at the root screen. */
  handleBack: (): void => {
    const history = get().screenHistory as string[];
    if (history.length > 1) {
      set({
        screen: history[history.length - 2],
        screenHistory: history.slice(0, -1),
      });
    }
  },

  toggleDarkMode: (): void =>
    set((state: { darkMode: boolean }) => ({ darkMode: !state.darkMode })),

  setTheme: (theme: string): void => set({ theme }),

  setIsDragging: (isDragging: boolean): void => set({ isDragging }),

  setIsFetchingGps: (isFetchingGps: boolean): void => set({ isFetchingGps }),

  openFloorPlanDrawer: (projectId: number): void =>
    set({ floorPlanDrawerProjectId: projectId }),

  closeFloorPlanDrawer: (): void => set({ floorPlanDrawerProjectId: null }),
});
